// Groundworks & Excavation — per-template authored content. See ./types.ts for the content rule.
import type { TemplateSpecific } from "./types";

export const GROUNDWORKS: Record<string, TemplateSpecific> = {
  "ground-excavation": {
    whoFor:
      "If a principal contractor or client has asked for an excavation method statement before you break ground, this is the RAMS to hand them. It is written for groundworks gangs carrying out bulk and reduced-level dig on building plots, road and infrastructure works, where machines and people share the same ground. The reviewer wants to see that you have designed the excavation for stability and kept the plant/pedestrian interface controlled, not just listed generic excavation hazards.",
    scope:
      "This document covers general excavation works: bulk earthworks, reduced-level dig, foundation and footing excavation and the formation of battered or stepped faces. It assumes excavation by 360° tracked excavator with attendant banksman, with arisings either retained on site or removed by tipper. It does not cover the specific progressive support of narrow service trenches, confined-space entry into the excavation, or digging within the safe-dig zone of known live utilities — those are addressed by the trenching, confined-space and underground-services documents. Spoil stability, surface-water management and edge protection to any face deeper than 2m are within scope.",
    legislation: [
      "Construction (Design and Management) Regulations 2015, regulation 22 (excavations — prevent collapse, inspect, support)",
      "HSE HSG47 — Avoiding danger from underground services",
      "HSE HSG150 — Health and safety in construction",
      "BS 6031 — Code of practice for earthworks",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Work at Height Regulations 2005 (excavation edges and faces)",
    ],
    sequence: [
      "Carry out and record a CDM regulation 22 inspection by a competent person at the start of each shift before anyone enters the excavation, and again after any event likely to affect stability such as heavy rain.",
      "Set out the dig to the agreed batter angle or stepped profile from the temporary works design; never assume a face will stand vertically because it 'looks solid'.",
      "Establish exclusion zones and a one-way traffic plan separating the excavator slew radius from pedestrians, with a trained banksman directing all plant movements.",
      "Excavate in lifts, keeping spoil heaps at least the excavation depth back from the crest (minimum 2m) so surcharge load does not trigger collapse.",
      "Maintain edge protection and physical barriers to any open face a person could fall into, and provide stop blocks at tipping points.",
      "Manage groundwater and surface water by cut-off ditches or pumping so the face is not undermined; re-inspect after dewatering.",
      "Backfill, compact in layers and reinstate or hand over the formation, recording the final inspection before the excavation is signed off.",
    ],
    hazards: [
      {
        hazard: "Collapse of an unsupported or over-steep excavation face burying a worker",
        who: ["Groundworkers", "Banksman", "Site engineer"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Batter or bench faces to the temporary works design angle rather than excavating vertically",
          "Daily competent-person inspection under CDM regulation 22, recorded, before entry",
          "Keep spoil and plant loads back from the crest by at least the excavation depth (min 2m)",
          "Provide ladder or ramp access so workers are never trapped at the toe of a face",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Person struck by, or crushed against, the slewing excavator or its bucket",
        who: ["Groundworkers", "Banksman", "Visitors"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Physically segregate the slew radius with barriers and an exclusion zone",
          "Trained banksman to direct all machine movements using agreed signals",
          "Fit and use the excavator's audible travel/slew alarms and 360° camera",
          "No worker to enter the working envelope until the machine is stationary and acknowledged",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Plant or vehicle toppling into the excavation, or ground giving way under tracks",
        who: ["Excavator operator", "Tipper drivers", "Groundworkers"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Set and maintain stop blocks and a safe stand-off distance for plant at the crest",
          "Assess ground bearing capacity and use timber/mats where soft",
          "Keep tipping operations supervised by a banksman at a marked tipping point",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Workers and visitors falling into an open excavation, particularly out of hours",
        who: ["Groundworkers", "Other trades", "Public"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Provide double-rail edge protection or substantial barriers to all open faces",
          "Secure and light the excavation at the end of each shift",
          "Cover or fence small pits and pot-holes so they cannot be stepped into",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Face instability from groundwater ingress or surcharge after rainfall",
        who: ["Groundworkers", "Site engineer"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Provide cut-off ditches and sump pumping to keep water off the face",
          "Re-inspect under CDM reg 22 after heavy rain or any dewatering",
          "Reduce batter angle or install support if the face shows signs of ravelling",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
    ],
    ppe: [
      "Hard hat to EN 397",
      "Hi-vis clothing (upper and lower) for plant interface",
      "Safety boots with midsole and toe protection",
      "Gloves appropriate to handling tasks",
      "Eye protection when breaking out hard ground",
    ],
    competence: [
      "Plant operator holding a valid CPCS/NPORS card for 360° excavators",
      "Trained and appointed banksman/signaller",
      "Competent person nominated to carry out CDM reg 22 inspections",
      "Supervisor holding SSSTS or equivalent",
    ],
    permits: [
      "Permit to dig where any buried services may be present",
      "Excavation inspection record (CDM reg 22)",
    ],
    equipment: [
      "360° tracked excavator with quick-hitch and buckets",
      "Banksman radios and high-vis signalling kit",
      "Tipper lorries / dumpers for arisings",
      "Edge-protection barriers and stop blocks",
      "Sump pump and lay-flat hose for dewatering",
      "Access ladders or ramp for safe entry/egress",
    ],
    pcChecks: [
      "Whether the face profile (batter/bench angle) is justified by a temporary works design or competent-person assessment, not just asserted as 'safe'.",
      "That a named competent person and a CDM regulation 22 inspection record are built into the method, with a stated frequency and triggers.",
      "How the plant/pedestrian interface is segregated — physical exclusion zones and a banksman, not reliance on people 'keeping clear'.",
    ],
    faqs: [
      {
        q: "At what depth does an excavation need support?",
        a: "There is no magic depth in law — the popular '1.2m rule' is a myth. CDM 2015 regulation 22 requires you to take steps to prevent collapse at any depth that could bury or injure a person, and a face barely a metre high can do that in poor ground. Whether you batter, bench, or shore depends on the soil type, groundwater, surcharge from spoil and plant, and how long the excavation stays open. A competent person should assess each excavation rather than applying a single threshold.",
      },
      {
        q: "Do I have to inspect the excavation every day?",
        a: "Yes. CDM regulation 22 requires inspection by a competent person at the start of every shift before anyone works in the excavation, and again after any event that could have affected its stability — heavy rain, an impact, or fresh dewatering. The inspection must be recorded. You can build this record straight into the RamsDocs document so the daily check is signed off on site.",
      },
    ],
  },

  "ground-trenching": {
    whoFor:
      "Narrow trenches kill more groundworkers than any other excavation because they look controllable and collapse without warning. This trenching and shoring RAMS is for gangs installing progressive support — trench boxes, drag boxes or sheets and props — as the dig advances, typically for services, cables or strip foundations. A principal contractor reviewing it is looking for a method that supports the trench before anyone goes in, not after.",
    scope:
      "This document covers the excavation and progressive support of narrow trenches using proprietary trench boxes or hydraulic waling/strut systems, with the support installed and removed in step with the dig. It addresses craning trench boxes in and out, ladder access at the required spacing, and working safely between supports. It does not cover bulk open-cut excavation, confined-space entry classified under CSR 1997, or the safe-dig procedures for working near live utilities. Trenches deeper than the available box height or in unstable ground requiring a bespoke temporary works design are outside scope until that design is provided.",
    legislation: [
      "Construction (Design and Management) Regulations 2015, regulation 22 (excavation support and inspection)",
      "HSE HSG185 — Health and safety in excavations",
      "BS 6031 — Code of practice for earthworks",
      "Lifting Operations and Lifting Equipment Regulations 1998 (LOLER) — lifting trench boxes",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
      "Work at Height Regulations 2005 (ladder access to the trench)",
    ],
    sequence: [
      "Confirm the trench box / shoring system is rated for the trench depth and ground, and that a temporary works design or manufacturer's data sheet covers the installation.",
      "Excavate the first box length and lower the trench box into the open dig with the excavator under LOLER-compliant lifting, before anyone enters.",
      "Pin or push the box to the formation and check it sits proud of the surface to retain spoil and prevent material falling in.",
      "Provide a secured ladder giving access within 7.5m of any working position, extending at least 1m above the box top.",
      "Excavate the next length ahead, advance or add boxes, and only work between the installed supports — never in the unsupported lead.",
      "Remove boxes in reverse order as you backfill and compact, keeping the supported zone always ahead of the workers.",
      "Record the CDM reg 22 inspection of the support system at the start of each shift and after any disturbance.",
    ],
    hazards: [
      {
        hazard: "Trench collapse burying a worker in the unsupported section ahead of the boxes",
        who: ["Groundworkers", "Pipe layers", "Site engineer"],
        initial: { likelihood: 5, severity: 5 },
        controls: [
          "Install proprietary trench boxes or shoring before any entry, advancing support ahead of the work",
          "Prohibit working or reaching into the unsupported lead beyond the last box",
          "Competent-person CDM reg 22 inspection of the support each shift, recorded",
          "Use a box height that stands proud of the surface so spoil cannot ravel in",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Trench box or shoring component falling during lifting in or out",
        who: ["Groundworkers", "Excavator operator", "Slinger"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Lift boxes only with LOLER-rated chains/lifting points and a trained slinger",
          "Keep all personnel clear of the trench and the suspended load during box placement",
          "Inspect lifting accessories before use and within their certification date",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Crushing between the trench box wall and the excavation side during installation",
        who: ["Groundworkers", "Excavator operator"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "No one in the trench while a box is being lowered, pushed or repositioned",
          "Banksman to direct the operator and confirm the trench is clear before each movement",
          "Use spreaders/struts to control box spacing rather than hands or feet",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Fall while climbing into or out of the trench, or no escape in an emergency",
        who: ["Groundworkers", "Pipe layers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Provide a tied ladder within 7.5m of every working position, extending 1m above the box",
          "Maintain three points of contact and keep the ladder clear of spoil",
          "Replace ladders as the trench advances so access is never left behind the supported zone",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Spoil, tools or materials falling onto workers from the trench edge",
        who: ["Groundworkers", "Pipe layers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Keep spoil heaps and stacked materials at least 2m back from the trench edge",
          "Rely on the box standing proud of ground level to act as a toe-board",
          "Lower tools and materials in rather than throwing them into the trench",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Hard hat to EN 397",
      "Hi-vis clothing (upper and lower)",
      "Safety boots with midsole and toe protection",
      "Cut-resistant gloves for handling boxes and struts",
      "Eye protection when cutting or breaking out",
    ],
    competence: [
      "Excavator operator with valid CPCS/NPORS for 360° machines and a slinger/signaller qualification",
      "Operatives trained in the specific trench box / shoring system in use",
      "Competent person for CDM reg 22 support inspections",
      "Supervisor holding SSSTS or equivalent",
    ],
    permits: [
      "Permit to dig where buried services may be present",
      "Excavation/support inspection record (CDM reg 22)",
      "Lifting plan for trench box installation (LOLER)",
    ],
    equipment: [
      "Proprietary trench boxes or drag boxes (rated for depth)",
      "Hydraulic waling frames, struts and spreader bars",
      "LOLER-certified lifting chains and shackles",
      "Trench-access ladders for spacing within 7.5m",
      "360° excavator for digging and box placement",
      "Plate compactor for backfilling in layers",
    ],
    pcChecks: [
      "That the support is installed before entry and advanced ahead of the work — the method must rule out working in the unsupported lead.",
      "Whether the chosen trench box is rated for the trench depth and ground, with a manufacturer data sheet or temporary works design referenced.",
      "That ladder access is provided within 7.5m of every working position and is relocated as the trench advances, not left behind.",
    ],
    faqs: [
      {
        q: "Can I work in a trench without a trench box if it's not very deep?",
        a: "Only if a competent person has assessed that the trench cannot collapse and bury or injure anyone — which in most made ground or granular soils means you cannot. CDM regulation 22 places the duty on preventing collapse at any depth, and trenches well under a metre have killed workers. A trench box is usually the quickest and most reliable way to discharge that duty, and it also gives you a guaranteed safe place to stand. Do not rely on the trench 'standing up while you nip in'.",
      },
      {
        q: "How is lifting a trench box covered by the RAMS?",
        a: "Lowering and lifting a trench box with the excavator is a lifting operation under LOLER 1998, so it needs rated lifting accessories, a trained slinger and a lifting plan. The RAMS should state that no one is in the trench while a box is being placed, pushed or removed, and that the excavator operator works to a banksman's signals. RamsDocs lets you attach the manufacturer's lifting data and your inspection records alongside the method so the principal contractor sees the whole picture in one document.",
      },
    ],
  },

  "ground-confined-space": {
    whoFor:
      "Entering a chamber, manhole, tank or deep shaft on a groundworks site is a confined-space entry under the Confined Spaces Regulations 1997, and the principal contractor will not accept a generic excavation RAMS for it. This document is for trained entry teams carrying out low to medium-risk entries — atmosphere testing, top-man supervision, escape sets and pre-arranged rescue. The whole point of this RAMS is the breathable atmosphere and the rescue plan, not the risk of the walls falling in.",
    scope:
      "This document covers entry into confined spaces such as manholes, valve chambers, wet wells and shafts where the principal hazard is the atmosphere rather than structural collapse. It is written for low and medium-risk entries managed with continuous gas monitoring, a top-man, escape breathing apparatus and pre-arranged rescue. It does not cover high-risk entries requiring working breathing apparatus, hot work inside the space, or entries into spaces of unknown or actively dangerous atmosphere — those require a separate specialist assessment and a bespoke rescue capability. Any entry is preceded by a specific entry permit issued for that space.",
    legislation: [
      "Confined Spaces Regulations 1997, regulation 4 (avoid entry where reasonably practicable) and regulation 5 (emergency arrangements)",
      "HSE ACOP L101 — Safe work in confined spaces",
      "Management of Health and Safety at Work Regulations 1999 (risk assessment)",
      "Dangerous Substances and Explosive Atmospheres Regulations 2002 (DSEAR)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH)",
      "Construction (Design and Management) Regulations 2015",
    ],
    sequence: [
      "First eliminate the need to enter — can the task be done from outside? Only proceed to entry where it cannot reasonably be avoided (CSR reg 4).",
      "Issue a confined-space entry permit for the specific space and confirm rescue arrangements are in place before entry (CSR reg 5).",
      "Bump-test and calibrate the multi-gas monitor, then test the atmosphere from the surface for oxygen, flammables (LEL), hydrogen sulphide and carbon monoxide before anyone descends.",
      "Ventilate or purge the space as required and re-test; do not enter until readings are within safe limits.",
      "Brief the team on roles — entrant(s), top-man at the access point maintaining communication, and the rescue team — and don escape sets and harness/winch where used.",
      "Enter with the gas monitor worn in the breathing zone and monitoring continuously; the top-man logs entry/exit and never leaves the access point.",
      "On any alarm, fault or loss of communication, evacuate immediately and do not re-enter until the space is re-tested and the cause resolved.",
    ],
    hazards: [
      {
        hazard: "Toxic, flammable or oxygen-deficient atmosphere causing collapse or asphyxiation",
        who: ["Entrants", "Top-man", "Rescue team"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Avoid entry where reasonably practicable (CSR reg 4) — do the work from outside if possible",
          "Bump-test then test the atmosphere from the surface before entry and monitor continuously inside",
          "Forced ventilation/purging and re-test before entry, with immediate evacuation on any alarm",
          "Entrants wear escape breathing apparatus suited to the assessed hazard",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Failure or absence of rescue arrangements when an entrant is overcome",
        who: ["Entrants", "Rescue team", "Top-man"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Rescue arrangements in place and rehearsed BEFORE entry, not relying on the public fire service (CSR reg 5)",
          "Tripod/winch and harness for non-entry retrieval where the space allows",
          "Trained rescue team and resuscitation equipment on standby at the access point",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Free-flowing solids or sudden ingress of liquid (e.g. into a wet well or live chamber)",
        who: ["Entrants", "Top-man"],
        initial: { likelihood: 3, severity: 5 },
        controls: [
          "Isolate, lock off and confirm zero flow on any inlet/pump feeding the space before entry",
          "Monitor levels and maintain a clear evacuation route during the task",
          "Top-man to abort entry if conditions change",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Loss of communication between the entrant and the top-man",
        who: ["Entrants", "Top-man"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Maintain continuous voice or radio communication and an agreed signalling system",
          "Top-man remains at the access point throughout and never enters to assist alone",
          "Evacuate on any loss of communication and re-establish before re-entry",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Fall while accessing the space via fixed or portable ladder/shaft",
        who: ["Entrants"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Use harness and fall-arrest/winch on vertical access where a fall is possible",
          "Inspect fixed ladders and step irons before reliance; use a portable ladder if defective",
          "Keep the access clear of obstructions and well lit",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
    ],
    ppe: [
      "Escape breathing apparatus appropriate to the assessed risk",
      "Full-body harness with winch/retrieval line",
      "Multi-gas detector worn in the breathing zone",
      "Intrinsically safe lighting and radios",
      "Chemical-resistant gloves and coveralls where contamination is present",
    ],
    competence: [
      "Entrants and top-man trained and certificated for confined-space entry (medium risk)",
      "Trained rescue team competent in the rescue plan and resuscitation",
      "Gas monitor user trained in bump-testing and interpreting readings",
      "Authorised permit issuer for confined-space entry",
    ],
    permits: [
      "Confined-space entry permit (per space, per shift)",
      "Isolation/lock-off permit for any inlets or pumps",
    ],
    equipment: [
      "Calibrated multi-gas detector (O2, LEL, H2S, CO) plus bump-test gas",
      "Escape breathing apparatus / self-rescuers",
      "Tripod, winch and rescue harnesses",
      "Forced-ventilation blower and ducting",
      "Intrinsically safe lamps and two-way radios",
      "Resuscitation kit and first-aid equipment at the access point",
    ],
    pcChecks: [
      "Emergency arrangements that do not rely on the public fire service — CSR 1997 regulation 5 requires rescue arrangements to be in place and rehearsed BEFORE entry.",
      "That the method genuinely tries to avoid entry first (reg 4), and that the residual entry is justified rather than assumed.",
      "Atmosphere testing detail — that the monitor is bump-tested, what gases are tested for, and that monitoring is continuous inside, not a one-off reading at the top.",
    ],
    faqs: [
      {
        q: "Is a deep manhole automatically a confined space?",
        a: "It is a confined space if it is substantially enclosed and there is a reasonably foreseeable specified risk — most commonly a dangerous atmosphere, free-flowing solids, or drowning. A deep chamber or wet well almost always qualifies, but so can shallow ones if sewer gas can accumulate. The test is the risk, not the depth or the size. If in doubt, treat it as a confined space and apply the Confined Spaces Regulations 1997.",
      },
      {
        q: "Can we rely on calling 999 for rescue?",
        a: "No. Regulation 5 of the Confined Spaces Regulations 1997 requires suitable and sufficient emergency arrangements to be in place before anyone enters, and dialling 999 is not an arrangement — the fire service may be minutes away and may not be equipped for your specific space. You must provide your own trained rescue capability, retrieval equipment and resuscitation kit, and rehearse the plan. The atmosphere that overcame your entrant will overcome an untrained would-be rescuer just as quickly.",
      },
    ],
  },

  "ground-drainage": {
    whoFor:
      "Drainage installation looks like routine groundwork until you cut into a live foul sewer or lift a manhole ring by hand. This drainage installation method statement is for groundworks gangs laying pipework, building chambers and connecting to existing systems, where the standout risks are biological contamination and heavy manual handling rather than deep collapse. It gives the principal contractor the controls they expect to see for Weil's disease and for mechanically handling pipes and manhole rings.",
    scope:
      "This document covers the installation of below-ground drainage: laying foul and surface-water pipework on a prepared bed, building inspection chambers and manholes, benching and connecting into existing or live drainage systems, and air/water testing the completed runs. It assumes work in trenches supported by the separate trenching RAMS where depth requires it, and it focuses on the drainage-specific hazards of live sewers, biological agents and material handling. It does not cover confined-space entry into existing chambers, deep shaft sinking, or excavation within the safe-dig zone of unidentified services. Connection to a live sewer is by prior agreement with the asset owner.",
    legislation: [
      "Construction (Design and Management) Regulations 2015 (including regulation 22 where trenching is involved)",
      "Control of Substances Hazardous to Health Regulations 2002 (COSHH — biological agents)",
      "HSE guidance on leptospirosis (Weil's disease) in sewer and waterside work",
      "Manual Handling Operations Regulations 1992",
      "BS EN 1610 — Construction and testing of drains and sewers",
      "Provision and Use of Work Equipment Regulations 1998 (PUWER)",
    ],
    sequence: [
      "Set out the run to the design levels and falls, using a pipe laser or level set in or over the trench.",
      "Excavate to formation, support the trench per the trenching RAMS, and prepare and compact the granular pipe bed.",
      "Lay pipes mechanically using laying tongs or the excavator with a sling — never manhandle large-diameter pipes into the trench.",
      "Build chambers and lift manhole rings, cover slabs and frames mechanically with rated lifting accessories rather than by hand.",
      "Bench and haunch the chamber, make the connection into the existing system, and where the sewer is live agree isolation/over-pumping with the asset owner first.",
      "Air-test or water-test the completed run to BS EN 1610 and record the result before backfilling.",
      "Backfill and compact in layers, reinstate, and wash down before any breaks or eating to control biological exposure.",
    ],
    hazards: [
      {
        hazard: "Leptospirosis (Weil's disease) and other biological infection from contact with foul water or sewage",
        who: ["Drainage operatives", "Pipe layers"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Avoid skin contact with foul water — over-pump or isolate live flows before working",
          "Cover cuts and grazes with waterproof dressings before starting",
          "Provide chemical-resistant gloves, coveralls and welfare for washing before eating/smoking",
          "Issue Leptospira awareness cards so symptoms are reported and treated early",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Manual handling injury lifting heavy pipes, manhole rings, cover slabs and frames",
        who: ["Drainage operatives", "Pipe layers"],
        initial: { likelihood: 4, severity: 3 },
        controls: [
          "Use the excavator with chains, slings or a manhole-ring lifter for all heavy components",
          "Use pipe-laying tongs for laying pipe rather than lifting by hand into the trench",
          "Reserve manual handling for light items only, with team lifts and good technique",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Uncontrolled discharge or flooding when connecting into a live foul sewer",
        who: ["Drainage operatives", "Site engineer"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Agree isolation or over-pumping with the asset owner before breaking into a live system",
          "Plan the connection for low-flow periods and keep a pump on standby",
          "Maintain a clear escape route from the trench in case of sudden ingress",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Slips, trips and falls on a wet, muddy and obstructed drainage working area",
        who: ["Drainage operatives", "Other trades"],
        initial: { likelihood: 4, severity: 2 },
        controls: [
          "Keep pipe lengths, fittings and tools stacked clear of walking routes",
          "Pump standing water and lay boards over soft ground around the trench",
          "Maintain edge protection and lighting around open chambers and trenches",
        ],
        residual: { likelihood: 2, severity: 2 },
      },
      {
        hazard: "Compressed-air release or component ejection during air-testing of the run",
        who: ["Drainage operatives"],
        initial: { likelihood: 2, severity: 3 },
        controls: [
          "Use test plugs rated for the pressure and fit safety pins/lanyards",
          "Keep personnel clear of plug ends while the run is pressurised",
          "Release pressure in a controlled way before removing plugs",
        ],
        residual: { likelihood: 1, severity: 3 },
      },
    ],
    ppe: [
      "Waterproof gloves and chemical-resistant coveralls for foul water",
      "Safety boots with midsole and toe protection",
      "Hard hat and hi-vis clothing",
      "Eye protection for cutting and testing",
      "Waterproof dressings for cuts and grazes",
    ],
    competence: [
      "Excavator/slinger competence for mechanical handling of pipes and rings (CPCS/NPORS)",
      "Operatives briefed on Weil's disease symptoms and reporting",
      "Drainage operatives competent in pipe laying and testing to BS EN 1610",
      "Supervisor holding SSSTS or equivalent",
    ],
    permits: [
      "Permit / agreement from the asset owner to connect into a live sewer",
      "Permit to dig where buried services may be present",
    ],
    equipment: [
      "Pipe laser or trench level for setting falls",
      "Pipe-laying tongs and manhole-ring lifting clamp",
      "Drainage rods, test plugs and air-test gauge",
      "Over-pump and lay-flat hose for live-flow diversion",
      "Granular bedding material and compaction plate",
      "Welfare/wash-down facilities for biological control",
    ],
    pcChecks: [
      "That mechanical handling is the default for pipes, manhole rings and cover slabs — the method must not rely on operatives lifting heavy components by hand.",
      "Biological-hazard controls for foul water — Weil's disease awareness, wash facilities and an isolation/over-pumping plan for live sewers.",
      "That connection into a live system is agreed with the asset owner and planned for low flow, with a standby pump.",
    ],
    faqs: [
      {
        q: "What controls do I need for Weil's disease on a drainage job?",
        a: "Leptospirosis is spread through contact with water or sewage contaminated by infected animal urine, so the controls are about keeping it off your skin and out of cuts. Over-pump or isolate live flows, cover any cuts or grazes with waterproof dressings, and provide waterproof gloves, coveralls and somewhere to wash before eating, drinking or smoking. Issue operatives with a Leptospira awareness card so that flu-like symptoms are reported and treated promptly. It is a COSHH biological hazard and must be assessed as one.",
      },
      {
        q: "Do I have to test the drainage before backfilling?",
        a: "Yes — drains and sewers should be air or water tested to BS EN 1610 before they are covered, both to prove the joints and to satisfy building control and the principal contractor. Testing afterwards means excavating again to fix any failure, so it is built into the sequence before backfill. Record the test result against the run; you can capture it directly in the RamsDocs document so the evidence sits with the method statement.",
      },
    ],
  },

  "ground-underground-services": {
    whoFor:
      "Striking a buried cable or gas main is one of the most common serious incidents in groundworks, and it is almost always preventable. This working near underground services RAMS is built around the safe-digging process — survey records, CAT and Genny scanning, trial holes and hand digging — for any gang excavating where live utilities may be present. It is the document a principal contractor expects before issuing a permit to dig.",
    scope:
      "This document covers the avoidance of danger from underground services during excavation: obtaining and interpreting utility records and PAS 128 survey data, locating services with a cable avoidance tool and signal generator, proving their position by hand-dug trial holes, and excavating safely around the located apparatus. It applies wherever electricity cables, gas, water, telecoms or other buried services may be present. It does not cover the diversion or disconnection of services (a utility-owner activity), street works requiring NRSWA accreditation beyond the operatives' duties, or works inside the apparatus itself. All excavation under this document is subject to a permit to dig.",
    legislation: [
      "HSE HSG47 — Avoiding danger from underground services",
      "Construction (Design and Management) Regulations 2015",
      "PAS 128 — Specification for underground utility detection, verification and location",
      "HSE CIS64 — Construction information sheet: avoiding danger from underground services",
      "Electricity at Work Regulations 1989",
      "New Roads and Street Works Act 1991 (NRSWA) where works are in the highway",
    ],
    sequence: [
      "Obtain and review utility records and any PAS 128 survey for the area before any excavation, and mark up a service drawing.",
      "Scan the dig area with a cable avoidance tool (CAT) and signal generator (Genny) in all modes — power, radio and signal — and mark located services on the ground.",
      "Issue a permit to dig that records the survey, the scan results and the safe-digging precautions for the specific dig.",
      "Excavate trial holes by hand or vacuum excavation to positively prove the position and depth of each located service.",
      "Hand-dig within 500mm of any indicated or known service, using insulated tools and avoiding picks/forks directly over the line.",
      "Support, protect and re-mark exposed services, and re-scan if the dig extends beyond the originally surveyed area.",
      "Backfill with the correct surround and marker tape/tiles, and reinstate to the asset owner's requirements.",
    ],
    hazards: [
      {
        hazard: "Striking a live electricity cable causing explosion, burns or electrocution",
        who: ["Groundworkers", "Excavator operator", "Public"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Locate with CAT & Genny and prove position by hand-dug trial holes before machine digging",
          "Hand-dig with insulated tools within 500mm of any indicated or known cable",
          "Keep the excavator clear of the proven line; no picking or spiking over a service",
          "Issue a permit to dig capturing the survey and safe-dig method",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Rupturing a gas main causing fire, explosion or asphyxiating release",
        who: ["Groundworkers", "Excavator operator", "Public"],
        initial: { likelihood: 4, severity: 5 },
        controls: [
          "Confirm gas-main position from records and PAS 128 survey, then prove by trial hole",
          "Hand-dig around the main and avoid any ignition source if a leak is suspected",
          "Stop work, evacuate and call the gas emergency line on any smell or hiss of gas",
        ],
        residual: { likelihood: 2, severity: 5 },
      },
      {
        hazard: "Relying on inaccurate or missing utility records and digging blind",
        who: ["Groundworkers", "Site engineer"],
        initial: { likelihood: 4, severity: 4 },
        controls: [
          "Treat all records as indicative only and always confirm on the ground with CAT & Genny",
          "Assume unrecorded services may be present and dig as if a service is there",
          "Re-survey if the excavation extends beyond the area covered by the records",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
      {
        hazard: "Striking a water main or other pressurised service causing flooding or jetting",
        who: ["Groundworkers", "Excavator operator"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Locate and trial-hole water and telecoms services as for any other utility",
          "Hand-dig to expose and protect the service before continuing the machine dig",
          "Keep a clear escape route and stop pumps/flows where isolation is possible",
        ],
        residual: { likelihood: 2, severity: 3 },
      },
      {
        hazard: "Misuse of, or over-reliance on, the cable avoidance tool giving false confidence",
        who: ["Groundworkers", "Site engineer"],
        initial: { likelihood: 3, severity: 4 },
        controls: [
          "Only trained, competent operatives to use CAT & Genny, checking the daily function/battery test",
          "Scan in all modes and from multiple directions, not power mode alone",
          "Always confirm CAT readings with a hand-dug trial hole — the tool indicates, it does not prove",
        ],
        residual: { likelihood: 2, severity: 4 },
      },
    ],
    ppe: [
      "Insulated hand tools for digging near services",
      "Hard hat to EN 397 and hi-vis clothing",
      "Safety boots with midsole and toe protection",
      "Arc-rated/flame-resistant clothing where high-voltage cables may be present",
      "Eye protection and cut-resistant gloves",
    ],
    competence: [
      "Operatives trained and competent in CAT & Genny use (e.g. EUSR/manufacturer scheme)",
      "Excavator operator with valid CPCS/NPORS for 360° machines",
      "Permit-to-dig issuer competent in HSG47 safe-digging practice",
      "NRSWA street-works qualified operatives where the work is in the highway",
    ],
    permits: [
      "Permit to dig (per excavation, recording survey and scan results)",
      "Hot-works/ignition controls if a gas leak risk is identified",
    ],
    equipment: [
      "Cable avoidance tool (CAT) and signal generator (Genny)",
      "PAS 128 survey records and marked-up service drawings",
      "Insulated spades, shovels and forks for hand digging",
      "Vacuum excavation unit where used for trial holes",
      "Spray paint and markers for setting out located services",
      "Service protection covers, marker tape and tiles for reinstatement",
    ],
    pcChecks: [
      "That the method follows HSG47 in full — survey records, CAT & Genny location, trial holes and hand digging — not just a line saying 'avoid services'.",
      "Whether a permit to dig is issued per excavation, capturing the survey, the scan results and the safe-dig precautions for that specific dig.",
      "That CAT & Genny operators are trained and competent and that scans are confirmed by hand-dug trial holes rather than treated as proof of position.",
    ],
    faqs: [
      {
        q: "Do I need a permit to dig?",
        a: "If there is any chance of buried services in the area, yes — HSG47 expects a controlled system, and most principal contractors operate a permit to dig that the gang must hold before breaking ground. The permit ties together the utility records, the PAS 128 survey, the CAT and Genny results and the safe-digging precautions for that specific excavation, so it is the evidence that the avoidance process was actually followed. Even on a private site without a formal scheme you should record the same checks. RamsDocs includes a permit-to-dig section you can issue alongside this method statement.",
      },
      {
        q: "How close to a buried service can I use a machine?",
        a: "Use the excavator to remove overburden, but once you are within around 500mm of an indicated or known service you should stop machine digging and hand-dig with insulated tools to expose it. HSG47 is built on locating, then proving by trial hole, then digging carefully by hand near the apparatus — picks and forks should never be driven directly over a line. The exact stand-off can be greater for high-voltage cables or high-pressure gas, where the asset owner's requirements take precedence. Treat every located service as live until the owner confirms otherwise.",
      },
    ],
  },
};
